import React from 'react';

import ButtonBase from './button-base';
import { BaseButtonProps } from './types';

/**
 * Returns an instance of a SocialButton.
 *
 * @param btnText - Contents of the button.
 * @param className - Class name(s) to apply. This should include
 * the name of the social provider (e.g. `facebook`, `google`) so the corresponding
 * icon can be applied via CSS.
 * @param disabled - If true, sets `disabled` property on the button.
 * @param id - The value to set for the `id` property.
 * @param name - The value to set for the `name` property.
 * @param onClick - The callback handler for the click event.
 * @param type - Type property. Must be `button`, `reset`, or `submit`.
 * @param value - Value to set on the button.
 * @param props - Customization options passed to base button.
 * @returns React.ReactNode.
 *
 * @example
 *
 * ```typescriptreact
 * <SocialButton
 *   btnText="Social Button"
 * />
 * ```
 */
export function SocialButton({
  btnText,
  className,
  disabled,
  id,
  name,
  onClick,
  type,
  value,
  ...props
}: BaseButtonProps): React.ReactNode {
  return (
    <ButtonBase
      className={`social-button${className ? ` ${className}` : ''}`}
      disabled={disabled}
      id={id}
      name={name}
      onClick={onClick}
      title={btnText}
      type={type}
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <>
        <span className='icon' />
        <span className='text'>{btnText}</span>
      </>
    </ButtonBase>
  );
}
